import React, { useState, useEffect } from 'react';
import { Button, Space, Tag, Tooltip, message, Modal, Alert, Form, Input } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  ThunderboltOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import apiClient from '../../services/api';
import CompleteWorkRecordModal from './CompleteWorkRecordModal';
import WorkRecordDetailModal from './WorkRecordDetailModal';
import { ASSET_WORK_STATUS, STATUS_COLORS, ACTION_LABELS, SUCCESS_MESSAGES, ERROR_MESSAGES } from '../../constants/assetWork';

interface WorkRecordActionsProps {
  workRecord: {
    id: number;
    asset_code: string;
    asset_name: string;
    maintenance_type_name: string;
    work_description?: string;
    status: string;
    completed_date?: string;
    cost?: number;
    company_id?: number;
    asset_id?: number;
    work_documents?: string;
    work_date?: string;
    notes?: string;
    maintenance_campaign_id?: number;
    created_at?: string;
    updated_at?: string;
    employee_name?: string;
    supplier_name?: string;
  };
  onActionSuccess: (actionType: string) => void;
  companyId?: number;
}

const WorkRecordActions: React.FC<WorkRecordActionsProps> = ({
  workRecord,
  onActionSuccess,
  companyId
}) => {
  const [completeModalVisible, setCompleteModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [assetUser, setAssetUser] = useState<any>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  // Thêm state cho modal hoàn thành nhanh
  const [quickCompleteModalVisible, setQuickCompleteModalVisible] = useState(false);
  const [quickCompleteForm] = Form.useForm();
  const [quickCompleteLoading, setQuickCompleteLoading] = useState(false);

  const canComplete = [ASSET_WORK_STATUS.IN_PROGRESS, ASSET_WORK_STATUS.EXECUTION_APPROVED].includes(workRecord.status as any);
  const canDelete = [ASSET_WORK_STATUS.PENDING_EXECUTION_APPROVAL, ASSET_WORK_STATUS.IN_PROGRESS, ASSET_WORK_STATUS.EXECUTION_APPROVED].includes(workRecord.status as any);

  // Lấy thông tin nhân viên sử dụng tài sản
  const fetchAssetUser = async () => {
    if (!workRecord.asset_id) return;

    try {
      const asset = await apiClient.getAsset(workRecord.asset_id);
      if (asset && asset.assigned_to_employee_id) {
        setAssetUser(asset);
      }
    } catch (error) {
      console.error('Không thể lấy thông tin tài sản:', error);
    }
  };

  useEffect(() => {
    fetchAssetUser();
  }, [workRecord.asset_id]);

  const handleComplete = () => {
    setCompleteModalVisible(true);
  };

  const handleViewDetail = () => {
    setDetailModalVisible(true);
  };

  const handleQuickComplete = () => {
    setQuickCompleteModalVisible(true);
  };

  const handleQuickCompleteSubmit = async (values: any) => {
    setQuickCompleteLoading(true);
    try {
      await apiClient.completeWorkRecord(workRecord.id, {
        completed_date: dayjs().format('YYYY-MM-DD'),
        cost: 0,
        notes: 'Hoàn thành nhanh',
        work_documents: values.work_documents || null,
        performed_by_employee_id: assetUser?.assigned_to_employee_id || null,
        performed_by_supplier_id: null
      });

      onActionSuccess('quick_complete');
      message.success(SUCCESS_MESSAGES.COMPLETED);
      setQuickCompleteModalVisible(false);
      quickCompleteForm.resetFields();
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || ERROR_MESSAGES.COMPLETE_FAILED;
      message.error(errorMsg);
    } finally {
      setQuickCompleteLoading(false);
    }
  };

  const handleDelete = () => {
    setDeleteModalVisible(true);
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);
    try {
      await apiClient.deleteAssetWorkRecord(workRecord.id);
      setDeleteModalVisible(false);
      onActionSuccess('delete');
      message.success(SUCCESS_MESSAGES.DELETED);
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || ERROR_MESSAGES.DELETE_FAILED;
      message.error(errorMsg);
    } finally {
      setDeleting(false);
    }
  };

  const handleCompleteSuccess = () => {
    onActionSuccess('complete');
  };

  return (
    <>
      <Space size="small">
        {/* Action hoàn thành */}
        {canComplete && (
          <Tooltip title="Hoàn thành tác nghiệp">
            <Button
              type="primary"
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={handleComplete}
            />
          </Tooltip>
        )}

        {/* Action hoàn thành nhanh */}
        {canComplete && (
          <Tooltip title="Hoàn thành nhanh">
            <Button
              type="text"
              size="small"
              icon={<ThunderboltOutlined />}
              onClick={handleQuickComplete}
              style={{ color: '#52c41a' }}
            />
          </Tooltip>
        )}

        {/* Action xem chi tiết - luôn hiển thị */}
        <Tooltip title="Xem chi tiết tác nghiệp">
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={handleViewDetail}
            style={{ color: '#1890ff' }}
          />
        </Tooltip>

        {/* Action xóa tác nghiệp */}
        {canDelete && (
          <Tooltip title="Xóa tác nghiệp">
            <Button
              type="text"
              size="small"
              icon={<DeleteOutlined />}
              onClick={handleDelete}
              style={{ color: '#ff4d4f' }}
            />
          </Tooltip>
        )}

        {/* Hiển thị thông tin nếu đã hoàn thành */}
        {workRecord.status === ASSET_WORK_STATUS.COMPLETED && (
          <Space size="small">
            {workRecord.cost && workRecord.cost > 0 && (
              <Tooltip title="Chi phí thực tế">
                <Tag color="blue" icon={<ExclamationCircleOutlined />}>
                  {workRecord.cost.toLocaleString('vi-VN')} VNĐ
                </Tag>
              </Tooltip>
            )}
          </Space>
        )}
      </Space>

      {/* Modal hoàn thành nhanh */}
      <Modal
        title={
          <Space>
            <ThunderboltOutlined style={{ color: '#52c41a' }} />
            <span>Hoàn thành nhanh tác nghiệp</span>
          </Space>
        }
        open={quickCompleteModalVisible}
        onCancel={() => {
          setQuickCompleteModalVisible(false);
          quickCompleteForm.resetFields();
        }}
        footer={null}
        width={500}
        destroyOnClose
      >
        <Form
          form={quickCompleteForm}
          layout="vertical"
          onFinish={handleQuickCompleteSubmit}
        >
          <div style={{ marginBottom: 16 }}>
            <p><strong>Tài sản:</strong> {workRecord.asset_code} - {workRecord.asset_name}</p>
            <p><strong>Loại tác nghiệp:</strong> {workRecord.maintenance_type_name}</p>
          </div>

          <Form.Item
            label="Mã hồ sơ tác nghiệp"
            name="work_documents"
            rules={[{ required: true, message: 'Vui lòng nhập mã hồ sơ tác nghiệp' }]}
          >
            <Input
              placeholder="Nhập mã hồ sơ tác nghiệp"
              prefix={<FileTextOutlined />}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setQuickCompleteModalVisible(false);
                quickCompleteForm.resetFields();
              }}>
                Hủy
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={quickCompleteLoading}
                icon={<ThunderboltOutlined />}
              >
                Hoàn thành nhanh
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal hoàn thành tác nghiệp */}
      <CompleteWorkRecordModal
        visible={completeModalVisible}
        onCancel={() => setCompleteModalVisible(false)}
        onSuccess={handleCompleteSuccess}
        workRecord={workRecord}
        companyId={companyId}
      />

      {/* Modal xác nhận xóa tác nghiệp */}
      <Modal
        title={
          <Space>
            <DeleteOutlined style={{ color: '#ff4d4f' }} />
            <span>Xác nhận xóa tác nghiệp</span>
          </Space>
        }
        open={deleteModalVisible}
        onCancel={() => setDeleteModalVisible(false)}
        footer={null}
        width={500}
        destroyOnClose
      >
        <div style={{ marginBottom: 24 }}>
          <p>Bạn có chắc chắn muốn xóa tác nghiệp này không?</p>
          <div style={{
            background: '#f5f5f5',
            padding: 16,
            borderRadius: 8,
            marginBottom: 16
          }}>
            <div><strong>Tài sản:</strong> {workRecord.asset_code} - {workRecord.asset_name}</div>
            <div><strong>Loại tác nghiệp:</strong> {workRecord.maintenance_type_name}</div>
            <div><strong>Trạng thái hiện tại:</strong> {workRecord.status}</div>
          </div>
          <Alert
            message="Lưu ý"
            description="Tác nghiệp sẽ bị bỏ qua trong lần này. Bạn có thể hoàn thành vào lần sau khi tác nghiệp quá hạn trong thời gian tiếp theo."
            type="warning"
            showIcon
          />
        </div>

        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button onClick={() => setDeleteModalVisible(false)}>
              Hủy
            </Button>
            <Button
              type="primary"
              danger
              loading={deleting}
              icon={<DeleteOutlined />}
              onClick={handleConfirmDelete}
            >
              Xóa tác nghiệp
            </Button>
          </Space>
        </div>
      </Modal>

      {/* Modal xem chi tiết tác nghiệp */}
      <WorkRecordDetailModal
        visible={detailModalVisible}
        workRecord={workRecord}
        onClose={() => setDetailModalVisible(false)}
      />
    </>
  );
};

export default WorkRecordActions; 